<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada }"
      ref="formCardRef"
    >
      <!-- MENSAJE FINAL -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <p class="qr-header">¡Gracias!</p>
        <p class="qr-instructions">
          Gracias por confirmar tu asistencia.
        </p>
      </div>

      <!-- FORMULARIO -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirmar    <br> Asistencia
          </h2>
          <br>

          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
          <p class="form-subtitle">Paso {{ currentStep }} de {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1 -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label class="form-label">Nombre</label>
              <input
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Escribe tu nombre"
                required
              >
            </div>
            <div class="form-group">
              <label class="form-label">Teléfono</label>
              <input
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="Celular a 10 dígitos"
                required
              >
            </div>
          </div>

          <!-- Paso 2 -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <label class="form-label">¿Asistirás?</label>
            <div class="radio-group">
              <label><input type="radio" value="si" v-model="attending"> Sí</label>
              <label><input type="radio" value="no" v-model="attending"> No</label>
            </div>
          </div>

          <!-- Paso 3 -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <label class="form-label">¿Cuántos asistirán?</label>
            <select v-model.number="guests" class="form-select" required>
              <option :value="null" disabled>Selecciona una opción</option>
              <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <!-- Botones -->
          <div class="d-flex justify-content-between mt-3 buttons-row">
            <button
              v-if="currentStep > 1"
              type="button"
              class="submit-button secondary"
              @click="prevStep"
            >
              Atrás
            </button>

            <button
              v-if="currentStep < totalSteps"
              type="button"
              class="submit-button"
              @click="nextStep"
            >
              Siguiente
            </button>

            <button
              v-if="currentStep === totalSteps"
              type="submit"
              class="submit-button"
            >
              Confirmar
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">
            {{ errorMessage }}
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '@/api'
import gsap from 'gsap'

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: Number,
    nombre: String,
    idFestejado: Number
  },
  setup(props) {
    const name = ref('')
    const phone = ref('')
    const attending = ref('')
    const guests = ref(null)
    const guestsOptions = ref([1,2,3,4,5,6])
    const asistenciaConfirmada = ref(false)
    const currentStep = ref(1)
    const errorMessage = ref('')

    const formCardRef = ref(null)
    const tituloAnimado = ref(null)
    const containerRef = ref(null)

    const isValidInvitado = computed(() => Number(props.idInvitado) > 0)

    const totalSteps = computed(() => 3)
    const asistenciaStep = computed(() => 2)

    const nextStep = () => {
      errorMessage.value = ''

      if (currentStep.value === 1) {
        if (!name.value || !phone.value) {
          errorMessage.value = 'Escribe tu nombre y número de teléfono'
          return
        }
      }

      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Selecciona si asistirás o no'
          return
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value
          return
        }
      }

      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (!guests.value) {
          errorMessage.value = 'Selecciona cuántos asistirán'
          return
        }
      }

      currentStep.value++
    }

    const prevStep = () => {
      errorMessage.value = ''
      currentStep.value--
    }

    const handleSubmit = async () => {
      try {
        const payload = {
          nombre_asistencia: name.value,
          telefono_invitado: phone.value,
          asistira: attending.value,
          num_pases: attending.value === 'no' ? 0 : guests.value,
          id_festejada: props.idFestejado,
          invitedby: isValidInvitado.value ? (props.nombre || 'xv') : 'xv'
        }

        if (isValidInvitado.value) {
          payload.id_invitado = props.idInvitado
        }

        await axios.post(`${baseUrl}/asistencia`, payload)
        asistenciaConfirmada.value = true
      } catch (e) {
        errorMessage.value = 'Error al confirmar la asistencia'
      }
    }

    onMounted(async () => {
      if (!isValidInvitado.value) {
        guestsOptions.value = [1,2,3,4,5,6]
      }

      await nextTick()

      gsap.fromTo(
        formCardRef.value,
        { opacity: 0, y: 40, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power2.out' }
      )

      gsap.fromTo(
        tituloAnimado.value,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      )
    })

    return {
      name,
      phone,
      attending,
      guests,
      guestsOptions,
      asistenciaConfirmada,
      currentStep,
      totalSteps,
      asistenciaStep,
      nextStep,
      prevStep,
      handleSubmit,
      errorMessage,
      formCardRef,
      tituloAnimado,
      containerRef
    }
  }
}
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Great+Vibes&family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,400;1,500&family=Poppins:wght@400;500;600;700&display=swap');

/* CONTENEDOR BASE CON FONDO Y TEXTURA DE LA INVITACIÓN */
.confirmation-container {
  min-height: 70vh;
  padding: 3rem 1rem;
  background-color: #f5f0e6;
    display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}

.confirmation-card {
  width: 100%;
  max-width: 550px;
  background:#f5f0e6;
  border: 1.5px solid #cba56b;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(73, 121, 160, 0.12);
  padding: 2.5rem;
  backdrop-filter: blur(6px);
  box-sizing: border-box;
  transition: transform 0.2s ease;
}

/* ENCABEZADO FORMAL */
.form-header {
  margin-bottom: 2.5rem;
  text-align: center;
  border-bottom: 1px solid rgba(203, 165, 107, 0.4);
  padding-bottom: 1.5rem;
}

/* TÍTULO PRINCIPAL EN AZUL CELESTE EDITORIAL DE LA IMAGEN */
.form-title {
  font-family: 'Cinzel', 'Playfair Display', serif;
  font-size: 1.85rem !important;
  font-weight: 700;
  color: #4979a0;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08));
}

/* SUBTÍTULO EN CURSIVA DORADA CÁLIDA ESTILO SÁBADO/AÑO */
.form-subtitle {
  font-family: 'Great Vibes', cursive, serif;
  color: #cba56b;
  font-size: 1.8rem;
  line-height: 1.3;
  text-align: center;
  font-weight: 500;
}

/* GRUPOS DE FORMULARIO */
.form-group {
  margin-bottom: 1.75rem;
}

.form-label {
  display: block;
  margin-bottom: 0.6rem;
  font-family: 'Cinzel', 'Playfair Display', serif;
  font-weight: 700;
  font-size: 0.95rem;
  color: #4979a0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

/* CONTROLES DE ENTRADA CON DETALLES DORADOS */
.form-control,
.form-select {
  width: 100%;
  padding: 0.85rem 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  border: 1.5px solid #cba56b;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.85);
  color: #4979a0;
  box-sizing: border-box;
  transition: all 0.2s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #4979a0;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(73, 121, 160, 0.2);
  outline: none;
}

/* RADIO BUTTONS */
.radio-group {
  display: flex;
  gap: 2rem;
  padding-top: 0.5rem;
}

.radio-input {
  accent-color: #cba56b;
  transform: scale(1.1);
  cursor: pointer;
}

.radio-label {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  color: #4979a0;
  font-weight: 500;
  margin-left: 0.5rem;
  cursor: pointer;
}

/* BOTÓN DE ACCIÓN PRINCIPAL CON COLOR CELESTE Y BORDE DORADO */
.submit-button {
  width: 100%;
  padding: 1rem;
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #4979a0;
  background:#fff;
  border: 1.5px solid #cba56b;
  border-radius: 25px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(73, 121, 160, 0.15);
  transition: all 0.35s ease;
  margin-top: 1rem;
}

.submit-button:hover {
  background: #ffffff;
  transform: translateY(-2px);
  box-shadow: 
    0 6px 18px rgba(73, 121, 160, 0.25),
    0 0 12px rgba(203, 165, 107, 0.3);
}

.submit-button:disabled {
  background: #e0e8ef;
  border-color: #b0c4de;
  color: #8da4be;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* SECCIÓN QR / CONFIRMACIÓN EXITOSA */
.qr-view {
  text-align: center;
  padding: 1rem 0;
}

.qr-header {
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  color: #4979a0;
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
}

.qr-wrapper {
  background: #ffffff;
  padding: 1.5rem;
  display: inline-block;
  border: 1.5px solid #cba56b;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(73, 121, 160, 0.1);
  margin: 1.5rem 0;
}

.qr-code {
  width: 200px;
  height: 200px;
  border: none;
}

.event-title {
  font-family: 'Great Vibes', cursive, serif;
  font-weight: 500;
  font-size: 2.2rem;
  color: #cba56b;
  margin-top: 1rem;
}

/* BARRA DE PROGRESO CON DEGRADADO CELESTE Y DORADO */
.progress-container {
  height: 6px;
  background-color: rgba(203, 165, 107, 0.25);
  border-radius: 4px;
  margin-bottom: 2rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #cba56b, #4979a0);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* RESPONSIVE */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
    margin: 0.5rem;
  }
  
  .form-title {
    font-size: 1.45rem !important;
  }

  .form-subtitle {
    font-size: 1.5rem;
  }
}
</style>